/**
 * AVA-475 — open the selected reference drawing in a real browser window.
 *
 * WHY A WINDOW AND NOT THE IN-APP PANEL: a surveyor verifies against the ISO
 * sheet on a second monitor while the workspace keeps the 3D view. The existing
 * `FloatingDocumentViewer` is `position: absolute` inside the workspace column
 * and can never leave the tab, so it cannot serve that. It stays as the
 * FALLBACK — see `DrawingThumbnail.onExpand` — because a blocked pop-up or an
 * Office file must never leave the user with no way to see the drawing.
 *
 * THE POP-UP BLOCKER IS THE WHOLE DESIGN CONSTRAINT. Chrome only honours
 * `window.open` when it runs synchronously inside the user gesture. The expand
 * chain (button → `$emit('expand')` → parent handler) is synchronous today and
 * MUST STAY THAT WAY: `openDrawingPopout` is deliberately NOT `async`. It opens
 * an EMPTY window first and fills it afterwards from a detached promise. Anyone
 * "tidying" this into `async openDrawingPopout()` re-introduces the blocker.
 *
 * FOUR ORDERING/API RULES THAT LOOK LIKE STYLE AND ARE NOT:
 *
 *  1. No `noopener` / `noreferrer` in the feature string. The precedent at
 *     `Share/components/TagCard.vue:310` passes them, but with `noopener`
 *     `window.open` RETURNS NULL — indistinguishable from a blocked pop-up, and
 *     it throws away the handle we have to write the document into. This is
 *     same-origin content we generate ourselves, so there is nothing to isolate.
 *
 *  2. open window → `document.write` shell → `document.close()` → ONLY THEN
 *     `createObjectURL`. `document.write` implies `document.open()`, which
 *     REPLACES the document and silently revokes every blob URL registered
 *     against the old one. Registering first gives an `<iframe>` with a dead
 *     `src` and no error anywhere.
 *
 *  3. `popup.URL.createObjectURL`, never `window.URL`. The URL is then owned by
 *     the POPUP's document, so it survives the opener tab closing and is
 *     auto-revoked when the popup unloads. That is why there is no
 *     `revokeObjectURL` in this file — adding one would break the surviving-tab
 *     behaviour.
 *
 *  4. The PDF blob's MIME type is FORCED to `application/pdf`. `response.blob()`
 *     inherits Azure's `Content-Type`, which is frequently
 *     `application/octet-stream`; that will not render, and it also sidesteps a
 *     `Content-Disposition: attachment` turning the frame into a download.
 *
 * IMAGES ARE NOT FETCHED, DELIBERATELY. PDFs are already fetched cross-origin
 * from this origin today (`Documents/viewer/documentSource.js` fetchDocumentBlob), so
 * CORS is proven on that path. Images have only ever been loaded through `<img src>`
 * (`Documents/viewer/renderers/ImageRenderer.vue`); a `fetch()` of one could hit a CORS
 * rejection that an `<img>` never would. Do NOT "simplify" the two paths into
 * one fetch.
 */
import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';
import { getDocumentBlobUrl } from '@/utils/documentProxyUtils';
import { IMAGE_EXTENSIONS, PDF_EXTENSION, extensionOf } from '@/features/Verification/utils/referenceDrawing';

/**
 * ONE fixed window name, so a second expand REUSES the window instead of
 * papering the second screen with one window per click.
 */
const POPOUT_NAME = 'ava-verification-drawing';

/** Comfortable on a 1080p secondary display without covering it entirely. */
const POPOUT_MAX_WIDTH = 1280;
const POPOUT_MAX_HEIGHT = 960;
const POPOUT_WIDTH_RATIO = 0.8;
const POPOUT_HEIGHT_RATIO = 0.85;
const POPOUT_MIN_EDGE = 480;

/** The window we opened, so `closeDrawingPopout` cannot close someone else's. */
let activePopout = null;

/**
 * Monotonic ticket for the detached fill. Because the window is REUSED, a slow
 * fill for drawing A can land after the user has already expanded drawing B
 * into the same window — the handle is identical, so a handle comparison alone
 * would not catch it. Only the newest ticket is allowed to write.
 */
let fillSequence = 0;

const HTML_ESCAPES = Object.freeze({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
});

/**
 * The drawing name is tenant data and it is interpolated into a SAME-ORIGIN
 * document we write ourselves — an unescaped `<script>` in a file name would
 * execute with the application's origin. Escape every interpolation; status
 * messages use `textContent` instead and never come through here.
 */
function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (character) => HTML_ESCAPES[character]);
}

/**
 * Which renderer this document needs, or `null` when the pop-out cannot show it
 * (Office, unknown, no extension). The extension list comes from
 * `referenceDrawing.js` so this feature has ONE opinion about what an image is.
 *
 * @param {Object} doc - linked-document record from `state.linkedDocuments`
 * @returns {'pdf'|'image'|null}
 */
function popoutKind(doc) {
  if (!doc) return null;
  const extension = extensionOf(doc);
  if (extension === PDF_EXTENSION) return 'pdf';
  return IMAGE_EXTENSIONS.includes(extension) ? 'image' : null;
}

/**
 * True when the pop-out can actually render this document. `canExpand`
 * (`verificationContext.js:237`) only gates on a RETRIEVABLE source, so it lets
 * Office files through — those must fall back to the in-app viewer, which knows
 * how to hand them to Office Online.
 *
 * @param {Object} doc - linked-document record
 * @returns {boolean}
 */
export function isPopoutRenderable(doc) {
  return popoutKind(doc) !== null;
}

/**
 * The pop-out's entire document, as a self-contained string. No external
 * stylesheet, font or script: the window is written synchronously during the
 * click gesture and must be complete the instant it appears.
 *
 * BOTH renderers are present but hidden; the fill unhides the one it used. That
 * keeps `document.write` out of the async path, where re-writing the document
 * would revoke the blob URL we just registered (rule 2 in the module note).
 *
 * @param {Object} options
 * @param {string} options.title - drawing name, shown in the tab and header
 * @param {'pdf'|'image'} options.kind
 * @returns {string} complete HTML document
 */
export function buildPopoutHtml({ title, kind } = {}) {
  const safeTitle = escapeHtml(title || 'Reference drawing');
  const safeKind = escapeHtml(kind === 'pdf' ? 'PDF' : 'IMAGE');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${safeTitle}</title>
<style>
  * { box-sizing: border-box; }
  html, body { height: 100%; margin: 0; }
  body {
    display: flex;
    flex-direction: column;
    background: #22282b;
    color: #e9eeef;
    font-family: "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  }
  .ava-head {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 12px;
    background: #171c1e;
    border-bottom: 1px solid #000;
    font-size: 12px;
  }
  .ava-name {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: Consolas, "Courier New", monospace;
  }
  .ava-kind {
    flex: 0 0 auto;
    padding: 1px 7px;
    border: 1px solid #4d5a5e;
    border-radius: 2px;
    color: #9fb0b4;
    font-size: 10px;
    letter-spacing: 0.09em;
  }
  .ava-body {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #2c3336;
  }
  #ava-status {
    margin: 0;
    padding: 0 24px;
    max-width: 620px;
    text-align: center;
    font-size: 13px;
    line-height: 1.5;
    color: #c3cdd0;
  }
  #ava-frame {
    width: 100%;
    height: 100%;
    border: 0;
    background: #fff;
  }
  #ava-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    background: #fff;
  }
  [hidden] { display: none !important; }
</style>
</head>
<body>
<header class="ava-head">
  <span class="ava-name">${safeTitle}</span>
  <span class="ava-kind">${safeKind}</span>
</header>
<main class="ava-body">
  <p id="ava-status" role="status">Loading drawing…</p>
  <iframe id="ava-frame" title="${safeTitle}" hidden></iframe>
  <img id="ava-img" alt="${safeTitle}" hidden />
</main>
</body>
</html>`;
}

/**
 * Write a message into the pop-out's status line, or clear it with `''`.
 *
 * `textContent`, never `innerHTML` — messages carry caught error text, which is
 * not ours to trust. Every access is guarded because the user can close the
 * window at any point during the fill.
 */
function setPopoutStatus(popup, message) {
  try {
    if (!popup || popup.closed || !popup.document) return;
    const statusElement = popup.document.getElementById('ava-status');
    if (!statusElement) return;
    statusElement.textContent = message;
    statusElement.hidden = !message;
  } catch (err) {
    debugWarn('[DrawingPopout] could not update the pop-out status line', err);
  }
}

/**
 * True while `ticket` is still the newest fill AND the window is still the one
 * we opened and is still open. Called again after EVERY await — element
 * references must never be cached across one, because the document they belong
 * to can be replaced or destroyed in the meantime.
 */
function fillIsCurrent(popup, ticket) {
  return ticket === fillSequence && popup === activePopout && !popup.closed;
}

/**
 * Load the document into the already-open window. Detached on purpose: the
 * caller returns synchronously so the gesture that opened the window is never
 * left waiting on a network round trip.
 *
 * Failures are written into the window, not just logged. The user is looking at
 * a second screen; a white window reads as a hang. This is not hypothetical —
 * `getDocumentBlobUrl` falls back to the raw `storageReference` when the SAS
 * endpoint fails (`documentProxyUtils.js:90-94`), and that URL can then 403.
 */
async function fillPopout(popup, doc, kind, ticket) {
  try {
    const url = await getDocumentBlobUrl(doc);
    if (!fillIsCurrent(popup, ticket)) return;

    if (!url) {
      setPopoutStatus(popup, 'This drawing has no retrievable file.');
      return;
    }

    if (kind === 'image') {
      const image = popup.document.getElementById('ava-img');
      if (!image) return;
      // Ticket-checked too: an `<img>` detached by a later `document.write` can
      // still settle, and an unguarded handler would clear or overwrite the
      // status line belonging to the drawing that replaced it.
      image.onload = () => {
        if (fillIsCurrent(popup, ticket)) setPopoutStatus(popup, '');
      };
      image.onerror = () => {
        if (!fillIsCurrent(popup, ticket)) return;
        setPopoutStatus(
          popup,
          'The drawing could not be loaded. The file link may have expired — '
            + 'close this window and expand it again.',
        );
      };
      image.src = url;
      image.hidden = false;
      return;
    }

    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`);
    const buffer = await response.arrayBuffer();
    if (!fillIsCurrent(popup, ticket)) return;

    // Forced MIME type — see rule 4 in the module note.
    const blob = new Blob([buffer], { type: 'application/pdf' });
    // Registered against the POPUP's document, so no manual revoke — rule 3.
    const objectUrl = popup.URL.createObjectURL(blob);

    const frame = popup.document.getElementById('ava-frame');
    if (!frame) return;
    frame.src = objectUrl;
    frame.hidden = false;
    setPopoutStatus(popup, '');
    debugLog('[DrawingPopout] rendered PDF in the pop-out window');
  } catch (err) {
    debugWarn('[DrawingPopout] could not fill the pop-out window', err);
    // Guarded for the same reason as the image handlers: a superseded fill must
    // not report its failure over the drawing that replaced it.
    if (!fillIsCurrent(popup, ticket)) return;
    setPopoutStatus(
      popup,
      `This drawing could not be opened. ${err && err.message ? err.message : 'Unknown error.'}`,
    );
  }
}

/** Window size, clamped to whatever screen the browser reports. */
function popoutFeatures() {
  const screenInfo = (typeof window !== 'undefined' && window.screen) || {};
  const availableWidth = Number(screenInfo.availWidth) || POPOUT_MAX_WIDTH;
  const availableHeight = Number(screenInfo.availHeight) || POPOUT_MAX_HEIGHT;
  const width = Math.max(
    POPOUT_MIN_EDGE,
    Math.round(Math.min(POPOUT_MAX_WIDTH, availableWidth * POPOUT_WIDTH_RATIO)),
  );
  const height = Math.max(
    POPOUT_MIN_EDGE,
    Math.round(Math.min(POPOUT_MAX_HEIGHT, availableHeight * POPOUT_HEIGHT_RATIO)),
  );
  // No `noopener` — see rule 1 in the module note.
  return `popup=yes,resizable=yes,scrollbars=yes,width=${width},height=${height}`;
}

/**
 * Open (or reuse) the drawing pop-out.
 *
 * SYNCHRONOUS BY CONTRACT — `window.open` is the first thing that happens, and
 * nothing is awaited before it. Do not make this `async`.
 *
 * @param {Object} doc - linked-document record from `state.linkedDocuments`
 * @returns {Window|null} the window handle, or `null` when blocked/unrenderable
 */
export function openDrawingPopout(doc) {
  const kind = popoutKind(doc);
  if (!kind) return null;

  let popup = null;
  try {
    popup = window.open('', POPOUT_NAME, popoutFeatures());
  } catch (err) {
    debugWarn('[DrawingPopout] window.open threw', err);
    return null;
  }
  if (!popup || popup.closed) {
    debugWarn('[DrawingPopout] pop-up blocked — falling back to the in-app panel');
    return null;
  }

  const title = doc.fileName || doc.documentTitle || doc.documentNumber || 'Reference drawing';

  // Shell first, then close the stream, THEN the blob URL happens in the fill.
  // Reordering this drops the blob URL — see rule 2 in the module note.
  //
  // GUARDED FOR THE SAME REASON EVERY OTHER WINDOW ACCESS IN THIS FILE IS. The
  // window is reused by a FIXED NAME, so `window.open('', POPOUT_NAME, …)` can
  // hand back a window the user has since navigated somewhere else — Chrome
  // does not always honour `popup=yes` and may open a normal, navigable tab.
  // That handle is then CROSS-ORIGIN and `popup.document` throws. Returning
  // `null` keeps the function's one contract ("null ⇒ the caller falls back to
  // the in-app panel") instead of letting a `SecurityError` escape into the
  // click handler, where it would kill the fallback and leave the user with no
  // drawing at all. Everything below is deliberately skipped: a window we
  // cannot write to must not become `activePopout`, and filling it would only
  // churn through `setPopoutStatus`'s catch.
  try {
    popup.document.open();
    popup.document.write(buildPopoutHtml({ title, kind }));
    popup.document.close();
  } catch (err) {
    debugWarn(
      '[DrawingPopout] could not write the pop-out shell — falling back to the in-app panel',
      err,
    );
    return null;
  }
  try {
    popup.focus();
  } catch (err) {
    debugWarn('[DrawingPopout] could not focus the pop-out window', err);
  }

  activePopout = popup;
  fillSequence += 1;
  debugLog('[DrawingPopout] opened pop-out window for', title);
  // Intentionally not awaited: the caller must stay synchronous.
  fillPopout(popup, doc, kind, fillSequence);
  return popup;
}

/**
 * Close the pop-out we opened, if any.
 *
 * Safe to call when nothing is open — the workspace's `taskId` watcher is
 * `immediate: true`, so this runs once at mount before anything exists.
 */
export function closeDrawingPopout() {
  const popup = activePopout;
  activePopout = null;
  // Invalidate any fill still in flight, so it cannot write into a window the
  // user is about to see re-used for a different task's drawing.
  fillSequence += 1;
  if (!popup) return;
  try {
    if (!popup.closed) popup.close();
  } catch (err) {
    debugWarn('[DrawingPopout] could not close the pop-out window', err);
  }
}
